"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { ToneBank } from "@/components/tools/lit-essays/tone-bank";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  SOAPSTONE_TONE_WORDS,
  toggleTone,
} from "@/lib/tool-kit/essay/soapstone";
import {
  buildTpcastt,
  EMPTY_TPCASTT,
  TPCASTT_THESIS_FRAMES,
  type TpcasttInput,
} from "@/lib/tool-kit/lit-essays/tpcastt";

/** English strings. A translated content file overrides these through `ui`. */
export const TPCASTT_POETRY_ANALYSIS_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  basicsHeading: "The poem",
  titleLabel: "Title of the poem",
  titlePlaceholder: "e.g. Ozymandias",
  poetLabel: "Poet",
  poetPlaceholder: "e.g. Percy Bysshe Shelley",
  ruledLines: "Add ruled lines under empty steps (for handwriting)",
  stepHeadings: {
    title: "T — Title, before you read",
    paraphrase: "P — Paraphrase",
    connotation: "C — Connotation",
    attitude: "A — Attitude",
    shifts: "S — Shifts",
    titleAgain: "T — Title, again",
    theme: "T — Theme",
  },
  predictLabel: "What does the title lead you to expect?",
  predictPlaceholder:
    "e.g. A name I do not know, but it sounds ancient and grand — I expect a poem about a ruler or about power.",
  predictHint:
    "Write this before you read the poem. Think about the title’s literal meaning and what it suggests.",
  paraphraseLabel: "Paraphrase — the poem in your own words",
  paraphrasePlaceholder:
    "Restate each sentence or stanza in plain language. Say what happens, not what it means.",
  paraphraseHint:
    "Work sentence by sentence rather than line by line — a sentence can run across several lines.",
  connotationLabel:
    "Connotation — words and images that suggest more than they say",
  connotationPlaceholder:
    "e.g. “colossal Wreck” — size and ruin side by side\n“lone and level sands” — emptiness",
  connotationHint:
    "One per line, as “word or image — what it suggests”. Quote the poem exactly.",
  devicesLabel: "Poetic devices you noticed (optional)",
  devicesPlaceholder: "e.g. irony, imagery, alliteration",
  devicesHint: "Used in the thesis starter at the end.",
  attitudeLabel: "Attitude — the speaker’s tone",
  attitudePlaceholder: "e.g. ironic, reflective",
  attitudeHint:
    "Two or three precise words. Click words in the bank below to add or remove them.",
  attitudeEvidenceLabel: "What in the poem creates this tone? (optional)",
  attitudeEvidencePlaceholder:
    "Name the diction, imagery, or sentence structure that shows the attitude.",
  toneBankTitle: "Tone word bank — click to add or remove",
  toneGroups: {
    approving: "Approving",
    critical: "Critical",
    ironic: "Humorous or ironic",
    somber: "Somber",
    objective: "Objective",
    forceful: "Urgent or forceful",
    uneasy: "Uneasy or doubtful",
  },
  toneWords: SOAPSTONE_TONE_WORDS,
  shiftsLabel: "Shifts — where the poem changes",
  shiftsPlaceholder:
    "e.g. lines 1–8 — from the traveler’s description of the statue to what its face reveals\nlines 9–11 — from the ruin to the king’s boast",
  shiftsHint:
    "One per line, as “lines 9–11 — from … to …”. A shift can be a change in speaker, tone, subject, time, or image.",
  titleAgainLabel: "What does the title mean now?",
  titleAgainPlaceholder:
    "Reread the title and explain what new meaning it has taken on.",
  themeLabel: "Theme — one full sentence about life or people",
  themePlaceholder:
    "e.g. Human power is temporary, and time erases even the greatest rulers’ claims to be remembered.",
  themeHint:
    "A statement, not a word (“power”) and not a summary of the poem. Used in the thesis starter.",
  fileName: "tpcastt-poetry-analysis",
  document: {
    heading: "Your TPCASTT worksheet",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: step still to write|other: steps still to write",
    complete: "Every step has your notes.",
  },
  docTitle: "TPCASTT: “{title}”",
  docTitleBlank: "TPCASTT poetry analysis",
  subtitle: "by {poet} · seven-step poetry analysis",
  subtitleBlank: "Seven-step poetry analysis",
  steps: {
    title: {
      heading: "T — Title (before you read)",
      question:
        "What does the title lead you to expect? Consider its literal meaning and its connotations before you read a line.",
      prompt:
        "Write what the title makes you expect: the subject, the mood, any associations.",
    },
    paraphrase: {
      heading: "P — Paraphrase",
      question:
        "Restate the poem in your own plain words, sentence by sentence. Say what literally happens — do not interpret yet.",
      prompt:
        "Paraphrase the poem sentence by sentence. Keep it literal: who, what, where, and when.",
    },
    connotation: {
      heading: "C — Connotation",
      question:
        "Look past the literal meaning. Which words, images, comparisons, and sounds suggest more than they say?",
      prompt:
        "List key words or images, one per line, as “word or image — what it suggests”.",
    },
    attitude: {
      heading: "A — Attitude",
      question:
        "What is the speaker’s attitude toward the subject? Choose precise tone words, and name the diction that creates them.",
      prompt:
        "Choose two or three precise tone words, then point to the words and images that create them.",
    },
    shifts: {
      heading: "S — Shifts",
      question:
        "Where does the poem change — in speaker, tone, subject, time, or image? Watch for signal words (but, yet, however), punctuation, stanza breaks, and changes of tense.",
      prompt: "List each shift, one per line, as “lines 9–11 — from … to …”.",
    },
    titleAgain: {
      heading: "T — Title (again)",
      question:
        "Reread the title. What does it mean now that you have read the poem? What has it gained?",
      prompt:
        "Explain what the title means now, and how that differs from your first guess.",
    },
    theme: {
      heading: "T — Theme",
      question:
        "State the theme as a complete sentence about life or people — not one word, and not a summary of what happens.",
      prompt:
        "Write the theme as one full sentence that a reader could apply beyond this poem.",
    },
  },
  toneLabel: "Tone",
  and: "and",
  thesisHeading: "From notes to a thesis",
  thesisGuidance:
    "Two thesis starters built from your notes — rewrite one in your own words and check that it makes a claim a reader could argue with.",
  thesisPrompt:
    "Add the poem’s title and a theme sentence to see thesis starters.",
  missing: {
    title: "[title]",
    poet: "[poet]",
    devices: "[poetic devices]",
    tone: "[tone]",
    shift: "[shift]",
    theme: "[theme]",
  },
  thesisFrames: TPCASTT_THESIS_FRAMES,
  example: {
    title: "Ozymandias",
    poet: "Percy Bysshe Shelley",
    predict:
      "A name I do not recognize, but it sounds ancient and grand, like a king or a god. I expect a poem about power, or about a famous ruler.",
    paraphrase:
      "The speaker meets a traveler who describes two huge stone legs standing in the desert with no body above them. Nearby, half buried in the sand, lies a broken stone face with a frown and a sneer, which shows the sculptor understood the king’s arrogance. On the base is an inscription: the king, Ozymandias, tells other powerful rulers to look at what he built and despair. But nothing else is left. All around the wreck, empty sand stretches into the distance.",
    connotation:
      "“trunkless legs” — a body broken apart; power reduced to fragments\n“sneer of cold command” — arrogance and cruelty; a ruler who commanded by fear\n“Look on my Works, ye Mighty, and despair!” — a boast that now reads as irony\n“colossal Wreck” — enormous size and ruin side by side\n“lone and level sands” — emptiness; time has leveled everything",
    devices: "irony, imagery, alliteration",
    attitude: "ironic, reflective",
    attitudeEvidence:
      "The speaker passes the story along through a traveler and keeps a calm, distant voice, but words like “sneer” and “Wreck” undercut the king’s pride. The gap between the boast and the empty desert is the poem’s central irony.",
    shifts:
      "lines 1–8 — from the traveler’s description of the broken statue to what its face reveals about the king\nlines 9–11 — from the ruin to the king’s own words on the pedestal, a boast of power\nlines 12–14 — from the boast to the plain fact that nothing remains, ending on the empty desert",
    titleAgain:
      "“Ozymandias” is the Greek name for the pharaoh Ramesses II, so the title names the very king whose pride the poem undercuts. A name meant to inspire awe now labels a ruin.",
    theme:
      "Human power and pride are temporary, and time erases even the greatest rulers’ claims to be remembered.",
  },
} as const;

type TextKey = keyof TpcasttInput;

export default function TpcasttPoetryAnalysisGeneratorWidget() {
  const strings = useToolUi(TPCASTT_POETRY_ANALYSIS_GENERATOR_STRINGS);
  const [input, setInput] = useState<TpcasttInput>(EMPTY_TPCASTT);
  const [ruledLines, setRuledLines] = useState(false);
  const doc = useMemo(
    () => buildTpcastt(input, { ruledLines }, strings),
    [input, ruledLines, strings]
  );
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const area = (
    key: TextKey,
    label: string,
    placeholder: string,
    hint: string | undefined,
    rows = 3
  ) => (
    <TextAreaField
      hint={hint}
      id={`tpc-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={input[key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput({ ...strings.example })}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY_TPCASTT)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <Panel title={strings.basicsHeading}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="tpc-title"
              label={strings.titleLabel}
              onChange={update("title")}
              placeholder={strings.titlePlaceholder}
              value={input.title}
            />
            <TextField
              id="tpc-poet"
              label={strings.poetLabel}
              onChange={update("poet")}
              placeholder={strings.poetPlaceholder}
              value={input.poet}
            />
          </div>
          <Checkbox
            checked={ruledLines}
            label={strings.ruledLines}
            onChange={setRuledLines}
          />
        </Panel>
        <Panel title={strings.stepHeadings.title}>
          {area(
            "predict",
            strings.predictLabel,
            strings.predictPlaceholder,
            strings.predictHint,
            2
          )}
        </Panel>
        <Panel title={strings.stepHeadings.paraphrase}>
          {area(
            "paraphrase",
            strings.paraphraseLabel,
            strings.paraphrasePlaceholder,
            strings.paraphraseHint,
            5
          )}
        </Panel>
        <Panel title={strings.stepHeadings.connotation}>
          {area(
            "connotation",
            strings.connotationLabel,
            strings.connotationPlaceholder,
            strings.connotationHint,
            5
          )}
          <TextField
            hint={strings.devicesHint}
            id="tpc-devices"
            label={strings.devicesLabel}
            onChange={update("devices")}
            placeholder={strings.devicesPlaceholder}
            value={input.devices}
          />
        </Panel>
        <Panel title={strings.stepHeadings.attitude}>
          <TextField
            hint={strings.attitudeHint}
            id="tpc-attitude"
            label={strings.attitudeLabel}
            onChange={update("attitude")}
            placeholder={strings.attitudePlaceholder}
            value={input.attitude}
          />
          <ToneBank
            groups={strings.toneGroups}
            onToggle={(word) =>
              setInput((previous) => ({
                ...previous,
                attitude: toggleTone(previous.attitude, word),
              }))
            }
            title={strings.toneBankTitle}
            tone={input.attitude}
            words={strings.toneWords}
          />
          {area(
            "attitudeEvidence",
            strings.attitudeEvidenceLabel,
            strings.attitudeEvidencePlaceholder,
            undefined,
            2
          )}
        </Panel>
        <Panel title={strings.stepHeadings.shifts}>
          {area(
            "shifts",
            strings.shiftsLabel,
            strings.shiftsPlaceholder,
            strings.shiftsHint,
            4
          )}
        </Panel>
        <Panel title={strings.stepHeadings.titleAgain}>
          {area(
            "titleAgain",
            strings.titleAgainLabel,
            strings.titleAgainPlaceholder,
            undefined,
            2
          )}
        </Panel>
        <Panel title={strings.stepHeadings.theme}>
          {area(
            "theme",
            strings.themeLabel,
            strings.themePlaceholder,
            strings.themeHint,
            2
          )}
        </Panel>
      </div>
      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="tpc"
        labels={strings.document}
      />
    </div>
  );
}
